import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle, ArrowDown, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui';
import { MessageItem, type MessageView } from './MessageItem';

const NEAR_BOTTOM = 96;

export function MessageList({
  chatId,
  messages,
  streaming,
  error,
  onRetry,
  header,
}: {
  chatId: string;
  messages: MessageView[];
  streaming: boolean;
  error: { message: string; retryable: boolean } | null;
  onRetry: () => void;
  header?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  // Messages present when the chat opened do not animate in; later ones do.
  const initialIds = useRef<{ chatId: string; ids: Set<string> } | null>(null);
  if (!initialIds.current || initialIds.current.chatId !== chatId) {
    initialIds.current = { chatId, ids: new Set(messages.map((m) => m.id)) };
  }

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // New chat: jump to the end instantly.
  useLayoutEffect(() => {
    stick.current = true;
    scrollToBottom();
  }, [chatId, scrollToBottom]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM;
    stick.current = near;
    setAtBottom(near);
  }, []);

  // Follow growth (streaming text, images, markdown layout) while the user has not scrolled away.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => {
      if (stick.current) scrollToBottom();
      else onScroll();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [scrollToBottom, onScroll]);

  // When the user sends a message, always snap back to the bottom.
  const lastUserId = [...messages].reverse().find((m) => m.role === 'user')?.id;
  useEffect(() => {
    stick.current = true;
    scrollToBottom(true);
  }, [lastUserId, scrollToBottom]);

  const lastIdx = messages.length - 1;
  const last = messages[lastIdx];
  const canRetryLast = !streaming && last?.role === 'assistant' && Boolean(last.error);

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={scrollRef} onScroll={onScroll} className="scroll-thin absolute inset-0 overflow-y-auto overscroll-contain" tabIndex={-1}>
        <div ref={contentRef} className="mx-auto flex w-full max-w-3xl flex-col gap-7 px-4 pb-8 pt-6 sm:px-6">
          {header}
          <div role="log" aria-live="off" aria-label="Conversation" className="flex flex-col gap-7">
            {messages.map((m, i) => (
              <MessageItem key={m.id} message={m} animate={!initialIds.current!.ids.has(m.id)} onRetry={i === lastIdx && canRetryLast ? onRetry : undefined} />
            ))}
          </div>
          {error && !streaming && !canRetryLast && (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-600 dark:bg-red-500/10 dark:text-red-300">
              <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">{error.message}</span>
              {error.retryable && (
                <Button size="sm" variant="outline" onClick={onRetry}>
                  <RotateCw className="size-3.5" /> Retry
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <AnimatePresence>
        {!atBottom && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center"
          >
            <Button
              variant="outline"
              size="sm"
              className="pointer-events-auto rounded-full bg-surface shadow-lift"
              onClick={() => {
                stick.current = true;
                scrollToBottom(true);
              }}
            >
              <ArrowDown className="size-4" /> Jump to latest
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
